/* eslint-disable react/display-name */
import React, { memo, useCallback, useRef, useState } from 'react'

import closeIcon from '@assets/images/pdf-editor-close-icon.svg'

import { FormStatus, ObservationStatus, TaskStatus } from '@types'

import { useMarkerHover } from '../../hooks/useMarkerHover.hook'
import { useMarkerStyles } from '../../hooks/useMarkerStyle.hook'
import { Marker } from '../../type'

import {
  BASE_LABEL_STYLES,
  CLOSE_ICON_IMAGE_STYLES,
  CLOSE_ICON_STYLES,
  getMarkerIconStyle,
  ICONS,
  LONG_PRESS_DURATION,
  STATUS_ICON_STYLES,
} from '../../constants'

import { CrossMarker } from './CrossMarker.component'
import { MarkerStatusIcon } from './MarkerStatusIcon.component'

interface MarkerComponentProps {
  marker: Marker
  isSelected: boolean
  isDragging: boolean
  canEdit?: boolean
  showDelete?: boolean
  onMouseDown?: (id: string) => void
  onClick?: (id: string) => void
  onDelete?: (id: string) => void
}

export const MarkerComponent = ({
  marker,
  isSelected,
  isDragging,
  canEdit = true,
  showDelete = false,
  onMouseDown,
  onClick,
  onDelete,
}: MarkerComponentProps) => {
  const markerRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<number | null>(null)
  const [isLongPress, setIsLongPress] = useState(false)
  const isHover = useMarkerHover(markerRef, isDragging)

  const markerStyles = useMarkerStyles(isSelected, isHover, marker)

  const handleDelete = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      event.stopPropagation()

      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      onDelete?.(marker.id)
    },
    [marker.id, onDelete],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      event.stopPropagation()
      event.preventDefault()

      setIsLongPress(false)

      timerRef.current = window.setTimeout(() => {
        setIsLongPress(true)
        onMouseDown?.(marker.id)

        timerRef.current = null
      }, LONG_PRESS_DURATION)
    },
    [marker.id, onMouseDown],
  )

  const handleMouseUp = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (event.touches.length > 1) return

      event.stopPropagation()

      setIsLongPress(false)

      timerRef.current = window.setTimeout(() => {
        if (event.touches.length > 1) return

        setIsLongPress(true)
        onMouseDown?.(marker.id)

        timerRef.current = null
      }, LONG_PRESS_DURATION)
    },
    [marker.id, onMouseDown],
  )

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      event.preventDefault()

      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null

        if (!isLongPress) {
          onClick?.(marker.id)
        }
      }
    },
    [isLongPress, marker.id, onClick],
  )

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()

      if (!isLongPress) {
        onClick?.(marker.id)
      }
    },
    [isLongPress, marker.id, onClick],
  )

  return (
    <>
      <div
        ref={markerRef}
        data-marker-id={marker.id}
        style={isDragging ? { display: 'none' } : markerStyles}
        onMouseDown={canEdit && !isDragging ? handleMouseDown : undefined}
        onMouseUp={canEdit && !isDragging ? handleMouseUp : undefined}
        onTouchStart={canEdit && !isDragging ? handleTouchStart : undefined}
        onTouchEnd={canEdit && !isDragging ? handleTouchEnd : undefined}
        onClick={!isDragging ? handleClick : undefined}
      >
        <MarkerContent
          marker={marker}
          isSelected={isSelected}
          isHover={isHover}
          canEdit={canEdit}
          onDelete={handleDelete}
          showDelete={showDelete}
        />
      </div>

      {isDragging && <CrossMarker x={marker.x} y={marker.y} />}
    </>
  )
}

const DeleteButton = memo(({ onDelete }: { onDelete: (e: React.MouseEvent | React.TouchEvent) => void }) => (
  <div style={CLOSE_ICON_STYLES} onClick={onDelete} onMouseDown={(e) => e.stopPropagation()} onTouchStart={onDelete}>
    <img src={closeIcon} style={CLOSE_ICON_IMAGE_STYLES} alt="close" />
  </div>
))

const StatusIcon = memo(({ status }: { status: ObservationStatus | FormStatus | TaskStatus }) => {
  return (
    <div style={STATUS_ICON_STYLES}>
      <MarkerStatusIcon status={status} />
    </div>
  )
})

const MarkerContent = ({
  marker,
  isSelected,
  isHover,
  canEdit,
  showDelete,
  onDelete,
}: {
  marker: Marker
  isSelected: boolean
  isHover: boolean
  canEdit: boolean
  showDelete: boolean
  onDelete: (e: React.MouseEvent | React.TouchEvent) => void
}) => {
  const status: ObservationStatus | FormStatus | TaskStatus | undefined = marker?.data?.status

  const isValidated = [
    ObservationStatus.StatusValidatedByAdmin,
    ObservationStatus.StatusValidatedByCustomer,
    ObservationStatus.StatusValidatedByReader,
    FormStatus.StatusDone,
    TaskStatus.StatusDone,
  ].includes(status as ObservationStatus | FormStatus | TaskStatus)

  const IconComponent = ICONS[marker.type]
  const Icon = IconComponent as React.ComponentType<any>

  const iconStyle = getMarkerIconStyle(marker)

  return (
    <>
      {(isSelected || isHover || showDelete) && canEdit && <DeleteButton onDelete={(e) => onDelete(e)} />}
      <span
        style={{
          ...BASE_LABEL_STYLES,
          color: isValidated ? '#49536F' : 'white',
          fontWeight: isSelected ? 500 : 400,
        }}
      >
        {marker.data?.idNumber ? `#${marker.data.idNumber}` : ''}
      </span>
      <Icon style={iconStyle} fill={isValidated ? '#49536F' : 'white'} />
      {marker.data?.status && <StatusIcon status={marker.data?.status} />}
    </>
  )
}
